/* eslint-disable @typescript-eslint/member-ordering */
import { ID } from '@datorama/akita';
import { oneLine } from '@angular-devkit/core/src/utils/literals';
import { DatePipe } from '@angular/common';

import { Entity, Column, OneToMany } from '../entity';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { IRepRosterSalesRep } from './repRoster.model';
import { IEntity } from '../interfaces/models';
import { Observable } from 'rxjs';
import { Walklist } from './walklist.model';
import { WalklistService } from '../services/walklist';
import { Result } from '../interfaces/helpers';
import { commonConstants } from '../constants/common.constants';

export interface IVendorSalesRep extends IEntity {
  name: string;
  vendorGroupName: string;
  vendorGroupVendor: string;
  vendorGroupId: string;
  vendorAccountName: string;
  role: string;
  status: string;
  isActive: boolean;
  storeCode: string;
  profile: string;
  federation: string;
  cpms: string;
  vendorManagerName: string;
  vendorManagerCPMS: string;
  telusManagerName: string;
  telusManagerId: string;
  manager: string;
  mobileNumber: string;
  email: string;
  walklists?: Observable<Result<Walklist>>;
}

enum WhereClauses {
  vendorAccountName = 'Vendor_Account_Name__c',
  vendorGroup = 'Vendor_Group__c',
  storeId = 'D2C_ICI_Code__c',
  cpmsId = 'CPMS_ID__c',
  fedId = 'Federation_ID__c',
  id = 'Id',
  name = 'Name',
}

const possibleActiveUsers = Object.freeze(['active']);

@Entity({
  name: PageTitles.vendorSalesRep,
  language: LanguagePage.vendorSalesRep,
})
export class VendorSalesRep implements IVendorSalesRep {
  static readonly whereClauses = WhereClauses;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  name: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
    form: {
      defaultPosition: 1,
    },
  })
  vendorGroupName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 3,
    },
    form: {
      defaultPosition: 2,
    },
  })
  vendorAccountName: string;

  @Column({
    type: ColumnType.boolean,
    table: {
      defaultPosition: 4,
    },
    form: {
      defaultPosition: 3,
    },
  })
  isActive: boolean;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
    },
    form: {
      defaultPosition: 5,
    },
  })
  storeCode: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 7,
      hide: true,
    },
    form: {
      defaultPosition: 6,
    },
  })
  role: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 8,
      hide: true,
    },
    form: {
      defaultPosition: 7,
    },
  })
  federation: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
    form: {
      defaultPosition: 4,
    },
  })
  cpms: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 9,
      hide: true,
    },
    form: {
      defaultPosition: 8,
    },
  })
  vendorManagerName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 10,
      hide: true,
    },
    form: {
      defaultPosition: 9,
    },
  })
  telusManagerName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 11,
      hide: true,
    },
    form: {
      defaultPosition: 10,
    },
  })
  mobileNumber: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 12,
      hide: true,
    },
    form: {
      defaultPosition: 11,
    },
  })
  email: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 13,
      hide: true,
    },
  })
  createdBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 14,
      hide: true,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 12,
    },
  })
  get createdField(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    const date = datepipe.transform(
      this.createdAt,
      commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
    );

    return `${this.createdBy} ${date.toString()}`;
  }

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 15,
      hide: true,
    },
  })
  modifiedBy: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 16,
      hide: true,
    },
  })
  modifiedAt: Date;

  @Column({
    type: ColumnType.string,
    form: {
      defaultPosition: 13,
    },
  })
  get modifiedField(): string {
    const datepipe: DatePipe = new DatePipe('en-CA');
    const date = datepipe.transform(
      this.modifiedAt,
      commonConstants.DATE_FORMATS.MMMdd_YYYY_HHmm_a
    );

    return `${this.modifiedBy} ${date.toString()}`;
  }

  @OneToMany({
    repository: WalklistService,
    expression: { vendorSalesReps: (i: VendorSalesRep) => [i.cpms] },
  })
  walklists?: Observable<Result<Walklist>>;

  id: ID;
  vendorGroupId: string;
  vendorGroupVendor: string;
  status: string;
  profile: string;
  vendorManagerCPMS: string;
  telusManagerId: string;
  manager: string;
  href?: string;

  constructor(
    vendorSalesRep: IVendorSalesRep = {
      id: '',
      name: '',
      vendorGroupName: '',
      vendorGroupVendor: '',
      vendorGroupId: '',
      vendorAccountName: '',
      role: '',
      status: '',
      isActive: false,
      storeCode: '',
      profile: '',
      federation: '',
      cpms: '',
      vendorManagerName: '',
      vendorManagerCPMS: '',
      telusManagerName: '',
      telusManagerId: '',
      manager: '',
      mobileNumber: '',
      email: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
    }
  ) {
    const {
      id,
      name,
      vendorGroupName,
      vendorGroupVendor,
      vendorGroupId,
      vendorAccountName,
      role,
      status,
      isActive,
      storeCode,
      profile,
      federation,
      cpms,
      vendorManagerName,
      vendorManagerCPMS,
      telusManagerName,
      telusManagerId,
      manager,
      mobileNumber,
      email,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
    } = vendorSalesRep;

    this.id = id;
    this.name = name;
    this.vendorGroupName = vendorGroupName;
    this.vendorGroupVendor = vendorGroupVendor;
    this.vendorGroupId = vendorGroupId;
    this.vendorAccountName = vendorAccountName;
    this.role = role;
    this.status = status;
    this.isActive = isActive;
    this.storeCode = storeCode;
    this.profile = profile;
    this.federation = federation;
    this.cpms = cpms;
    this.vendorManagerName = vendorManagerName;
    this.vendorManagerCPMS = vendorManagerCPMS;
    this.telusManagerName = telusManagerName;
    this.telusManagerId = telusManagerId;
    this.manager = manager;
    this.mobileNumber = mobileNumber;
    this.email = email;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;
  }

  get self() {
    return this.cpms;
  }

  static getVendorSalesRepQuery(
    id: string,
    whereProperty: WhereClauses = VendorSalesRep.whereClauses.vendorGroup,
    whereConditionalOperator: string = 'equals',
    isCount = false
  ) {
    let query = !isCount
      ? `SELECT
      Assigned_to_Store_Code__r.Team_Manager__r.Name,
      Assigned_to_Store_Code__r.Team_Manager__r.CPMS_ID__c,
      Assigned_to_Store_Code__r.TELUS_Manager__r.Name,
      Assigned_to_Store_Code__r.TELUS_Manager__r.EmployeeNumber,
      Id,
      CPMS_ID__c,
      D2C_ICI_Code__c,
      Federation_ID__c,
      Name,
      Manager__c,
      Mobile_Number__c,
      Profile__c,
      Status__c,
      User__r.Email,
      User__r.IsActive,
      User__r.State,
      User__r.Deactivation_Date__c,
      Vendor_Account_Name__c,
      Vendor_Group__r.Id,
      Vendor_Group__r.Name,
      Vendor_Group__r.Vendor__c,
      Role__c,
      CreatedBy.Name,
      CreatedDate,
      LastModifiedDate,
      LastModifiedBy.Name
    FROM Vendor_Group_Member__c`
      : oneLine`SELECT COUNT() FROM Vendor_Group_Member__c`;

    if (id && whereConditionalOperator === 'equals') {
      query = `${query} WHERE ${whereProperty} = '${id}'`;
    }

    if (id && whereConditionalOperator === 'contains') {
      query = `${query} WHERE ${whereProperty} IN ('${id}')`;
    }

    if (id && whereConditionalOperator === 'like') {
      query = `${query} WHERE ${whereProperty} like '%${id}%'`;
    }

    if (!isCount) {
      query = oneLine`${query} ORDER BY Status__c ASC, Name ASC`;
    }

    return query;
  }

  static createFromDTO(record: IRepRosterSalesRep): VendorSalesRep {
    return new VendorSalesRep({
      id: record.Id,
      name: record.Name,
      vendorAccountName: record.Vendor_Account_Name__c,
      vendorGroupName: record.Vendor_Group__r.Name,
      vendorGroupVendor: record.Vendor_Group__r.Vendor__c,
      vendorGroupId: record.Vendor_Group__r.Id,
      role: record.Role__c,
      status: record.Status__c,
      //This is by Rajesh understanding of an active user. We don't have to use the isActive flag
      // Active User: Active
      // Inactive User: On Leave, Suspended, Terminated, Deleted
      // DO NOT CHANGE THIS LINE
      isActive: record.Status__c
        ? possibleActiveUsers.includes(record.Status__c.toLowerCase())
        : false,
      storeCode: record.D2C_ICI_Code__c,
      federation: record.Federation_ID__c,
      profile: record.Profile__c,
      cpms: record.CPMS_ID__c,
      vendorManagerName:
        record.Assigned_to_Store_Code__r?.Team_Manager__r?.Name,
      vendorManagerCPMS:
        record.Assigned_to_Store_Code__r?.Team_Manager__r?.CPMS_ID__c,
      telusManagerName:
        record.Assigned_to_Store_Code__r?.TELUS_Manager__r?.Name,
      telusManagerId:
        record.Assigned_to_Store_Code__r?.TELUS_Manager__r?.EmployeeNumber,
      manager: record.Manager__c,
      mobileNumber: record.Mobile_Number__c,
      email: record.User__r?.Email,
      createdBy: record.CreatedBy.Name,
      createdAt: record.CreatedDate,
      modifiedBy: record.LastModifiedBy.Name,
      modifiedAt: record.LastModifiedDate,
    });
  }
}
